import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ImageUpload } from '@/components/ImageUpload';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { UserBadges } from '@/components/TrustBadge';
import { useNavigate } from 'react-router-dom';

export default function ProfilePage() {
  const { user, refreshMe } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({});
  const [cities, setCities] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [skillsText, setSkillsText] = useState('');
  const [languagesText, setLanguagesText] = useState('');

  useEffect(() => {
    if (!user) return;
    setForm({
      first_name: user.first_name || '', last_name: user.last_name || '', phone: user.phone || '',
      bio: user.bio || '', city: user.city || '', commune: user.commune || '',
      photo_base64: user.photo_base64 || null, years_experience: user.years_experience || 0,
      travel_radius_km: user.travel_radius_km || 10, availability_status: user.availability_status || 'actif',
    });
    setSkillsText((user.skills || []).join(', '));
    setLanguagesText((user.languages || ['Français']).join(', '));
  }, [user?.id]);

  useEffect(() => { api.get('/catalog/cities').then((r) => setCities(r.data.items || [])); }, []);
  useEffect(() => { const c = cities.find((x) => x.name === form.city); setCommunes(c?.communes || []); }, [form.city, cities]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (user.role === 'jobeur') {
        payload.skills = skillsText.split(',').map((s) => s.trim()).filter(Boolean);
        payload.languages = languagesText.split(',').map((s) => s.trim()).filter(Boolean);
      }
      await api.patch('/users/me', payload);
      await refreshMe();
      toast.success('Profil mis à jour');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 space-y-4" data-testid="profile-page">
      <h1 className="text-2xl font-bold">Mon profil</h1>
      <UserBadges user={user} />

      {user.role === 'jobeur' && !user.id_verified && (
        <Card className="p-4 bg-[#EAF3FF] border-[#C7E2FF]">
          <p className="text-sm text-[#0E3F80] font-medium">Votre identité n&apos;est pas encore vérifiée.</p>
          <Button size="sm" className="mt-2 bg-[#1677F2] hover:bg-[#0F5FCC]" onClick={() => nav('/verify-id')} data-testid="profile-verify-cta">Vérifier mon identité</Button>
        </Card>
      )}

      <Card className="p-4 space-y-4">
        <ImageUpload value={form.photo_base64} onChange={(v) => update('photo_base64', v)} label="Ajouter votre photo" testid="profile-photo" />

        <div className="grid grid-cols-2 gap-3">
          <div><Label>Prénom</Label><Input value={form.first_name || ''} onChange={(e) => update('first_name', e.target.value)} data-testid="profile-first-name" /></div>
          <div><Label>Nom</Label><Input value={form.last_name || ''} onChange={(e) => update('last_name', e.target.value)} data-testid="profile-last-name" /></div>
        </div>
        <div><Label>Téléphone</Label><Input value={form.phone || ''} onChange={(e) => update('phone', e.target.value)} data-testid="profile-phone" /></div>
        <div><Label>Présentation</Label><Textarea value={form.bio || ''} onChange={(e) => update('bio', e.target.value)} rows={3} placeholder="Présentez-vous en quelques phrases…" data-testid="profile-bio" /></div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Ville</Label>
            <select className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={form.city || ''} onChange={(e) => update('city', e.target.value)} data-testid="profile-city">
              <option value="">—</option>
              {cities.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <Label>Commune</Label>
            <select className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={form.commune || ''} onChange={(e) => update('commune', e.target.value)} data-testid="profile-commune">
              <option value="">—</option>
              {communes.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {user.role === 'jobeur' && (
          <>
            <div><Label>Compétences (séparées par des virgules)</Label><Input value={skillsText} onChange={(e) => setSkillsText(e.target.value)} placeholder="Plomberie, Installation sanitaire..." data-testid="profile-skills" /></div>
            <div><Label>Langues parlées</Label><Input value={languagesText} onChange={(e) => setLanguagesText(e.target.value)} placeholder="Français, Lingala, Swahili" data-testid="profile-languages" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Années d&apos;expérience</Label><Input type="number" min="0" value={form.years_experience || 0} onChange={(e) => update('years_experience', Number(e.target.value))} data-testid="profile-years" /></div>
              <div><Label>Rayon (km)</Label><Input type="number" min="1" value={form.travel_radius_km || 10} onChange={(e) => update('travel_radius_km', Number(e.target.value))} data-testid="profile-radius" /></div>
            </div>
            <div>
              <Label>Statut</Label>
              <select className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={form.availability_status || 'actif'} onChange={(e) => update('availability_status', e.target.value)} data-testid="profile-availability">
                <option value="actif">Actif — je prends des jobs</option>
                <option value="pause">En pause</option>
                <option value="vacances">En vacances</option>
              </select>
            </div>
          </>
        )}

        <Button onClick={save} disabled={saving} className="w-full bg-[#1677F2] hover:bg-[#0F5FCC]" data-testid="profile-save-btn">
          {saving ? 'Sauvegarde…' : 'Sauvegarder'}
        </Button>
      </Card>
    </div>
  );
}
